import { useContext, useCallback, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import logoBranca from "../../assets/logo-transparente.png";
import styles from "./Header.module.scss";

// ─── Constants ────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { to: "/painel", label: "Painel" },
  { to: "/planos", label: "Planos" },
  { to: "/mensagens", label: "Mensagens" },
];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Header() {
  const { logout, usuario } = useContext(AuthContext);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = useCallback(() => {
    logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  const toggleMenu = useCallback(() => setMenuOpen((prev) => !prev), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  return (
    <header className={styles.header} role="banner">
      <div className={styles.inner}>

        {/* ── Brand ── */}
        <Link to="/painel" className={styles.brand} aria-label="FluxoEasy — Ir para o painel">
          <img src={logoBranca} alt="FluxoEasy" className={styles.logo} />
        </Link>

        {/* ── Desktop nav ── */}
        <nav className={styles.nav} aria-label="Navegação principal">
          {NAV_LINKS.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`
              }
              onClick={closeMenu}
            >
              {label}
            </NavLink>
          ))}
        </nav>

        {/* ── Right slot ── */}
        <div className={styles.actions}>
          {usuario?.nome && (
            <span className={styles.userBadge} aria-label={`Usuário: ${usuario.nome}`}>
              {usuario.nome.split(" ")[0]}
            </span>
          )}

          <button
            type="button"
            className={styles.logoutBtn}
            onClick={handleLogout}
            aria-label="Sair da conta"
          >
            <LogoutIcon />
            <span>Sair</span>
          </button>

          {/* ── Mobile hamburger ── */}
          <button
            type="button"
            className={styles.hamburger}
            onClick={toggleMenu}
            aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
          >
            <span className={`${styles.bar} ${menuOpen ? styles.barOpen : ""}`} />
            <span className={`${styles.bar} ${menuOpen ? styles.barOpen : ""}`} />
            <span className={`${styles.bar} ${menuOpen ? styles.barOpen : ""}`} />
          </button>
        </div>
      </div>

      {/* ── Mobile nav drawer ── */}
      <nav
        id="mobile-nav"
        className={`${styles.mobileNav} ${menuOpen ? styles.mobileNavOpen : ""}`}
        aria-label="Navegação mobile"
        aria-hidden={!menuOpen}
      >
        {NAV_LINKS.map(({ to, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `${styles.mobileLink} ${isActive ? styles.mobileLinkActive : ""}`
            }
            onClick={closeMenu}
          >
            {label}
          </NavLink>
        ))}
        <button
          type="button"
          className={styles.mobileLogoutBtn}
          onClick={handleLogout}
          aria-label="Sair da conta"
        >
          Sair
        </button>
      </nav>
    </header>
  );
}

// ─── Icon ─────────────────────────────────────────────────────────────────────

function LogoutIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}